import { Component, Suspense } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { isChunkLoadError, recoverFromStaleChunk, hardReloadFromNetwork } from '../lib/chunkReload.ts';

function Fallback(): JSX.Element {
  return <div className="p-8 text-sm text-muted-foreground" role="status" aria-live="polite">Loading…</div>;
}

type Props = { children: ReactNode };
type State = { failed: boolean };

// Error boundary + Suspense wrapper for lazy-loaded routes. When a route's JS
// chunk fails to load (stale deploy / offline), a plain <Suspense> would let the
// error bubble up and blank the whole app — the intermittent "page loading
// error". This catches it: for a chunk-load error it reloads once to fetch the
// fresh shell (loop-guarded in recoverFromStaleChunk); for anything else it
// shows a recoverable message with a manual reload, never a dead white screen.
class ErrorBoundary extends Component<Props, State> {
  state: State = { failed: false };

  static getDerivedStateFromError(): State {
    return { failed: true };
  }

  componentDidCatch(error: Error, _info: ErrorInfo): void {
    // A stale-chunk error self-heals on reload; let the guarded reloader run.
    // If it declines (already reloaded recently), the manual UI below shows.
    if (isChunkLoadError(error)) recoverFromStaleChunk();
  }

  render(): ReactNode {
    if (this.state.failed) {
      return (
        <div className="p-8 text-sm text-muted-foreground" role="alert">
          <p>This page failed to load.</p>
          <button
            type="button"
            className="btn-secondary btn-sm mt-3"
            onClick={() => void hardReloadFromNetwork()}
          >
            Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export function LazyBoundary({ children }: Props): JSX.Element {
  return (
    <ErrorBoundary>
      <Suspense fallback={<Fallback />}>{children}</Suspense>
    </ErrorBoundary>
  );
}
